'use client';

import { useCallback, useEffect, useState } from 'react';
import type { RoleValue } from '@vaults/ui/management/UseOrganizationAccessManagementHook';

export interface RolePrivilegeInfo {
  id: string;
  description: string;
}

export type RolePrivilegesMap = Record<RoleValue, RolePrivilegeInfo[]>;

interface UseRolePrivilegesResult {
  loading: boolean;
  error: string | null;
  privileges: RolePrivilegesMap | null;
  getPrivilegesForRole: (role: RoleValue) => RolePrivilegeInfo[];
}

// Module-level cache to avoid refetching on every component mount
let cachedPrivileges: RolePrivilegesMap | null = null;
let fetchPromise: Promise<RolePrivilegesMap> | null = null;

async function fetchRolePrivileges(): Promise<RolePrivilegesMap> {
  // Return cached data if available
  if (cachedPrivileges) {
    return cachedPrivileges;
  }

  // If a fetch is already in progress, wait for it
  if (fetchPromise) {
    return fetchPromise;
  }

  // Start a new fetch
  fetchPromise = (async () => {
    const response = await fetch('/api/roles/privileges', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error('Failed to load role privileges');
    }
    const data = await response.json();
    cachedPrivileges = data.roles as RolePrivilegesMap;
    return cachedPrivileges;
  })();

  try {
    return await fetchPromise;
  } finally {
    fetchPromise = null;
  }
}

export function useRolePrivileges(): UseRolePrivilegesResult {
  const [privileges, setPrivileges] = useState<RolePrivilegesMap | null>(cachedPrivileges);
  const [loading, setLoading] = useState(!cachedPrivileges);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    if (cachedPrivileges) {
      setPrivileges(cachedPrivileges);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const data = await fetchRolePrivileges();
        if (active) {
          setPrivileges(data);
          setError(null);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : 'Failed to load role privileges');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const getPrivilegesForRole = useCallback(
    (role: RoleValue): RolePrivilegeInfo[] => {
      return privileges?.[role] ?? [];
    },
    [privileges]
  );

  return {
    loading,
    error,
    privileges,
    getPrivilegesForRole,
  };
}
